import { useEffect, useRef, useState } from 'react';

import {
  coverageHoursRoutingsWithout,
  isCoverageHoursPageEmptied,
} from './coverageHoursTableRows';
import { CoverageHoursRouting } from './useCoverageHours';

export type UseCoverageHoursOptimisticRowsArgs = {
  routings: CoverageHoursRouting[];
  page: number;
  setPage: (page: number) => void;
  refreshCoverageHours: () => Promise<void>;
};

export type UseCoverageHoursOptimisticRowsResult = {
  displayedRoutings: CoverageHoursRouting[];
  onOptimisticDelete: (routingId: string) => void;
  onDeleteError: (routingId: string) => void;
  onDeleteSuccess: () => Promise<void>;
};

export function useCoverageHoursOptimisticRows({
  routings,
  page,
  setPage,
  refreshCoverageHours,
}: UseCoverageHoursOptimisticRowsArgs): UseCoverageHoursOptimisticRowsResult {
  const [deletingRoutingIds, setDeletingRoutingIds] = useState<string[]>([]);

  const displayedRoutings = coverageHoursRoutingsWithout(
    routings,
    deletingRoutingIds
  );

  // Set when hiding the rows emptied a page past the first. The page change
  // refetches on its own, so the success path must not refetch the old offset
  // as well, and a failed delete has to land the user back where they were.
  const clampedFromPage = useRef<number | null>(null);

  const onOptimisticDelete = (routingId: string) => {
    setDeletingRoutingIds(prev => [...prev, routingId]);

    const remaining = coverageHoursRoutingsWithout(routings, [
      ...deletingRoutingIds,
      routingId,
    ]);

    clampedFromPage.current = isCoverageHoursPageEmptied(page, remaining)
      ? page
      : null;

    if (clampedFromPage.current !== null) {
      setPage(page - 1);
    }
  };

  const onDeleteError = (routingId: string) => {
    setDeletingRoutingIds(prev => prev.filter(id => id !== routingId));

    if (clampedFromPage.current !== null) {
      setPage(clampedFromPage.current);
      clampedFromPage.current = null;
    }
  };

  const onDeleteSuccess = async () => {
    if (clampedFromPage.current !== null) {
      clampedFromPage.current = null;

      return;
    }

    await refreshCoverageHours();
  };

  // A routing stays hidden until the server stops returning it, so a deleted
  // row never flashes back while the refetch is in flight.
  useEffect(() => {
    const served = new Set(routings.map(routing => routing.routingId));

    setDeletingRoutingIds(prev => {
      const next = prev.filter(routingId => served.has(routingId));

      return next.length === prev.length ? prev : next;
    });
  }, [routings]);

  return {
    displayedRoutings,
    onOptimisticDelete,
    onDeleteError,
    onDeleteSuccess,
  };
}
